import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../common/Modal";
import { useIndexDb } from "../../hooks/useIndexDb";
import { useChats } from "../../hooks/useChats";
import { toast } from "react-toastify";
import { canSaveAudioToDevice } from "../../utils/saveAudioFile";
import { canRetryFinalless, formatRecordingDuration } from "../../utils/recordingList";
import { patientForRecording } from "../../utils/recordingPatientName";
import { isRecordingAutoRecoverEnabledFromStorage } from "../../utils/recordingAutoRecoverFlag";
import { currentOrgId } from "../../utils/currentOrgId";
import { useBulkSaveRecordings } from "../../hooks/useBulkSaveRecordings";

function formatWhen(dateMs) {
  if (!dateMs) return "";
  try {
    // Compact, locale-aware: "Jun 26, 4:00 PM" — the full toLocaleString
    // ("6/26/2026, 4:00:06 PM") wrapped awkwardly in the narrow row.
    return new Date(dateMs).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (e) {
    return "";
  }
}

function formatDay(dateMs) {
  if (!dateMs) return "";
  try {
    return new Date(dateMs).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch (e) {
    return "";
  }
}

const PARK_REASON_KEYS = {
  "predates-encounter": "layout.recordingsManager.parkedPredatesEncounter",
  "patient-mismatch": "layout.recordingsManager.parkedPatientMismatch",
  "too-old": "layout.recordingsManager.parkedTooOld",
  "encounter-mismatch": "layout.recordingsManager.parkedEncounterMismatch",
};

// Cutoff for the bulk "delete older than" control. Long enough that a recording
// still worth chasing is never in range (the drain's own retry budget, the
// cross-device recovery poll and the clinician's memory of the encounter are all
// far shorter), short enough to actually clear a queue that has stopped draining.
// Exported for the regression test.
export const STALE_RECORDING_DAYS = 15;
const DAY_MS = 24 * 60 * 60 * 1000;

// Recordings old enough for the bulk delete. A row with NO usable date is never
// included: absence of a date is not evidence of age, and this control deletes.
function staleRecordings(list, now = Date.now()) {
  const cutoff = now - STALE_RECORDING_DAYS * DAY_MS;
  return list.filter((r) => Number.isFinite(r?.dateMs) && r.dateMs > 0 && r.dateMs < cutoff);
}

function RecordingsModal() {
  const { t } = useTranslation();
  const {
    showRecordingsModal,
    setShowRecordingsModal,
    recordings,
    refreshRecordings,
    retryRecording,
    retryAll,
    deleteRecording,
    deleteRecordings,
    exportRecording,
  } = useIndexDb();
  const { chats } = useChats();
  const [confirmId, setConfirmId] = useState(null);
  // Same pattern as confirmId, for "Attach anyway" on an identity-parked row.
  const [confirmAttachId, setConfirmAttachId] = useState(null);
  const [savingId, setSavingId] = useState(null);
  // Bulk save is shared with the sign-in backlog alert (useBulkSaveRecordings):
  // both offer it immediately before a bulk delete, so they must agree exactly on
  // what counts as saved — and `bulkSave` is the provider's flag, so a run started
  // from the alert is visible here (and disables the controls below) too.
  const { bulkSave, saveAll, saveAllLabel } = useBulkSaveRecordings();
  const [confirmDeleteOld, setConfirmDeleteOld] = useState(false);
  const [deletingOld, setDeletingOld] = useState(false);

  // See canSaveAudioToDevice: not offered inside the native shell, where the file is
  // already the app's own and an anchor download would do nothing.
  const canSaveAudio = !!exportRecording && canSaveAudioToDevice();

  const saveAudio = async (r) => {
    setSavingId(r.id);
    try {
      const saved = await exportRecording(r.keys, { patient: patientForRecording(chats, r).name });
      if (!saved) toast.error(t("layout.recordingsManager.saveFailed"));
    } finally {
      setSavingId(null);
    }
  };

  const deleteOld = async (rows) => {
    if (deletingOld) return;
    setDeletingOld(true);
    try {
      // One transaction for the whole set (deleteRecordings never throws; the
      // catch below is for the unexpected). Success is only announced when every
      // row went — the aged set is the one that most needs an honest count.
      const result = await deleteRecordings(rows.map((r) => r.keys));
      if (result?.ok) toast.success(t("layout.recordingsManager.deleteOldDone", { count: rows.length }));
      else toast.error(t("layout.recordingsManager.deleteOldFailed"));
    } catch (e) {
      console.error("Could not delete the aged recordings", e?.name || e);
      toast.error(t("layout.recordingsManager.deleteOldFailed"));
    } finally {
      setDeletingOld(false);
      setConfirmDeleteOld(false);
    }
  };

  // Re-read the queue each time the modal opens so the list is current.
  useEffect(() => {
    if (showRecordingsModal) refreshRecordings && refreshRecordings();
  }, [showRecordingsModal]); // eslint-disable-line react-hooks/exhaustive-deps

  const list = recordings || [];

  // If the row being confirmed for deletion leaves the list (e.g. it uploaded
  // via Retry all, or was deleted), drop the stale confirm state so it can't
  // linger and re-attach to a future row.
  useEffect(() => {
    if (confirmId && !list.some((r) => r.id === confirmId)) setConfirmId(null);
    if (confirmAttachId && !list.some((r) => r.id === confirmAttachId)) setConfirmAttachId(null);
  }, [list, confirmId, confirmAttachId]);

  const stale = staleRecordings(list);

  // The aged set can empty under an open confirm (a Retry all drained them, or the
  // drain's own purge ran) — don't leave a confirm offering to delete nothing.
  useEffect(() => {
    if (confirmDeleteOld && stale.length === 0 && !deletingOld) setConfirmDeleteOld(false);
  }, [stale.length, confirmDeleteOld, deletingOld]);

  const close = () => {
    setConfirmId(null);
    setConfirmAttachId(null);
    setConfirmDeleteOld(false);
    setShowRecordingsModal(false);
  };

  const bulkBusy = !!bulkSave || deletingOld;
  // While a bulk save is stitching recordings one by one, nothing that changes or
  // contends with the rows it is walking is offered: a Delete would pull a row out
  // from under the export (counted as a failure, or worse), a Retry could upload
  // and delete it, a second export would double the memory the sequential loop
  // exists to bound. The flag is the provider's, so this holds for a save started
  // from the sign-in alert as well.
  const saving = !!bulkSave;

  // Whether Retry means anything for an unfinished row here — see canRetryFinalless.
  // Resolved once per render, not per row: the kill-switch and the org are storage
  // reads. The org is the drain's own gate (it fails closed on a foreign or absent
  // stamp), read through the same helper it uses so the two cannot disagree.
  const autoRecoverEnabled = isRecordingAutoRecoverEnabledFromStorage();
  const isNativeShell = typeof window !== "undefined" && window.ReactNativeWebView !== undefined;
  const sessionOrgId = currentOrgId();
  const canRetry = (r) =>
    !r.isFinalless ||
    canRetryFinalless({ dateMs: r.dateMs, autoRecoverEnabled, isNativeShell, orgId: r.orgId, currentOrgId: sessionOrgId });

  return (
    <Modal
      show={!!showRecordingsModal}
      close={close}
      title={t("layout.recordingsManager.title")}
      size="small"
      action={
        <>
          <button className="white" onClick={close}>
            {t("layout.recordingsManager.close")}
          </button>
          <button onClick={() => retryAll && retryAll()} disabled={list.length === 0 || saving}>
            {t("layout.recordingsManager.retryAll")}
          </button>
        </>
      }
    >
      <div className="recordings-manager">
        {list.length === 0 && (
          <div className="recordings-empty">
            <i className="notranslate material-icons-outlined">graphic_eq</i>
            <span>{t("layout.recordingsManager.empty")}</span>
          </div>
        )}

        {/* Bulk actions. A queue that has stopped draining reaches hundreds of rows,
            and per-row controls are then not a way out — see the test file's note on
            how this list actually gets used when it is long. */}
        {list.length > 0 && (
          <div className={"recordings-bulk" + (confirmDeleteOld ? " confirming" : "")}>
            {!confirmDeleteOld && (
              <div className="bulk-actions">
                {canSaveAudio && (
                  <button className="rec-btn" onClick={() => saveAll(list)} disabled={bulkBusy}>
                    <i className="notranslate material-icons-outlined">download</i>
                    <span>{saveAllLabel(list.length)}</span>
                  </button>
                )}
                {stale.length > 0 && (
                  <button className="rec-btn danger" onClick={() => setConfirmDeleteOld(true)} disabled={bulkBusy}>
                    <i className="notranslate material-icons-outlined">delete_sweep</i>
                    <span>{t("layout.recordingsManager.deleteOld", { count: stale.length, days: STALE_RECORDING_DAYS })}</span>
                  </button>
                )}
              </div>
            )}

            {confirmDeleteOld && (
              <div className="recording-confirm">
                <span className="confirm-text">
                  {t("layout.recordingsManager.deleteOldConfirm", { count: stale.length, days: STALE_RECORDING_DAYS })}
                </span>
                <div className="confirm-actions">
                  <button className="rec-btn" onClick={() => setConfirmDeleteOld(false)} disabled={bulkBusy}>
                    <span>{t("layout.recordingsManager.cancel")}</span>
                  </button>
                  {/* Offered HERE too, for the same reason the per-row confirm offers
                      it: this is the last moment the audio still exists. */}
                  {canSaveAudio && (
                    <button className="rec-btn" onClick={() => saveAll(stale)} disabled={bulkBusy}>
                      <span>{saveAllLabel(stale.length)}</span>
                    </button>
                  )}
                  <button className="rec-btn danger-solid" onClick={() => deleteOld(stale)} disabled={bulkBusy}>
                    <span>{t("layout.recordingsManager.confirmDeleteOld", { count: stale.length })}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {list.map((r) => {
          const confirming = confirmId === r.id;
          const confirmingAttach = confirmAttachId === r.id;
          // The row's own label first: its chatId may now name another patient's
          // encounter (recycled ids). The lookup is only a fallback, and says so.
          const { name: patient, fromEncounter } = patientForRecording(chats, r);
          const duration = formatRecordingDuration(r.durationSecs);
          const durationLabel = duration && r.isSegmented ? "~" + duration : duration;
          const retryable = canRetry(r);
          const identityParked = r.parked === "identity";
          const target = r.parkTarget || {};
          const reasonKey = identityParked ? PARK_REASON_KEYS[r.parkReason] : null;
          return (
            <div className={"recording-row" + (confirming || confirmingAttach ? " confirming" : "")} key={r.id}>
              <div className="recording-main">
                <div className="recording-info">
                  {patient && (
                    <span
                      className={"recording-patient" + (fromEncounter ? " from-encounter" : "")}
                      title={fromEncounter ? t("layout.recordingsManager.patientFromEncounter") : undefined}
                    >
                      {patient}
                    </span>
                  )}
                  <span className="recording-meta">
                    {identityParked ? (
                      <span className="recording-status error">{t("layout.recordingsManager.parked")}</span>
                    ) : (
                      <span className={"recording-status " + r.status}>{t("layout.recordingsManager.status." + r.status)}</span>
                    )}
                    {/* No final segment ever landed: the length is a lower bound and
                        the drain can only take it through the finalless recovery
                        path, if that path will have it at all. */}
                    {r.isFinalless && <span className="recording-unfinished">{t("layout.recordingsManager.unfinished")}</span>}
                    {durationLabel && <span className="recording-len">{durationLabel}</span>}
                    <span className="recording-when">{formatWhen(r.dateMs)}</span>
                    {r.errorsCount > 0 && (
                      <span className="recording-attempts">
                        {t("layout.recordingsManager.attempts", { count: r.errorsCount })}
                      </span>
                    )}
                  </span>
                  {/* Retry would be a button that does nothing here (too old for the
                      rebuild window, kill-switch off, or the native shell), so it is
                      not shown; say what still works instead. */}
                  {!retryable && <span className="recording-hint">{t("layout.recordingsManager.unfinishedNoRetry")}</span>}
                  {reasonKey && (
                    <span className="recording-hint">
                      {t(reasonKey, { recorded: formatDay(r.dateMs), created: formatDay(target.createdOn) })}
                    </span>
                  )}
                </div>

                {!confirming && !confirmingAttach && (
                  <div className="recording-actions">
                    {retryable && (
                      <button
                        className="rec-btn"
                        onClick={() => (identityParked ? setConfirmAttachId(r.id) : retryRecording && retryRecording(r.keys))}
                        disabled={saving}
                      >
                        <i className="notranslate material-icons-outlined">refresh</i>
                        <span>{t("layout.recordingsManager.retry")}</span>
                      </button>
                    )}
                    {canSaveAudio && (
                      <button className="rec-btn" onClick={() => saveAudio(r)} disabled={savingId === r.id || saving}>
                        <i className="notranslate material-icons-outlined">download</i>
                        <span>
                          {savingId === r.id ? t("layout.recordingsManager.saving") : t("layout.recordingsManager.saveAudio")}
                        </span>
                      </button>
                    )}
                    <button className="rec-btn danger" onClick={() => setConfirmId(r.id)} disabled={saving}>
                      <i className="notranslate material-icons-outlined">delete_outline</i>
                      <span>{t("layout.recordingsManager.delete")}</span>
                    </button>
                  </div>
                )}
              </div>

              {confirmingAttach && (
                <div className="recording-confirm">
                  <span className="confirm-text">{t("layout.recordingsManager.attachConfirm")}</span>
                  <span className="confirm-text">
                    {t("layout.recordingsManager.attachRecording", {
                      date: formatDay(r.dateMs),
                      patient: patient || t("layout.recordingsManager.attachUnknown"),
                    })}
                  </span>
                  <span className="confirm-text">
                    {t("layout.recordingsManager.attachTarget", {
                      date: formatDay(target.createdOn),
                      patient: target.patientLabel || t("layout.recordingsManager.attachUnknown"),
                    })}
                  </span>
                  <div className="confirm-actions">
                    {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
                    <button className="rec-btn" autoFocus onClick={() => setConfirmAttachId(null)}>
                      <span>{t("layout.recordingsManager.keepParked")}</span>
                    </button>
                    <button
                      className="rec-btn danger-solid"
                      disabled={saving}
                      onClick={() => {
                        retryRecording && retryRecording(r.keys, { overrideIdentity: true });
                        setConfirmAttachId(null);
                      }}
                    >
                      <span>{t("layout.recordingsManager.attachAnyway")}</span>
                    </button>
                  </div>
                </div>
              )}

              {confirming && (
                <div className="recording-confirm">
                  <span className="confirm-text">{t("layout.recordingsManager.deleteConfirm")}</span>
                  <div className="confirm-actions">
                    <button className="rec-btn" onClick={() => setConfirmId(null)}>
                      <span>{t("layout.recordingsManager.cancel")}</span>
                    </button>
                    {/* Offered HERE too, deliberately: this is the one moment the audio
                        is about to stop existing, and it is the last chance to keep it. */}
                    {canSaveAudio && (
                      <button className="rec-btn" onClick={() => saveAudio(r)} disabled={savingId === r.id || saving}>
                        <span>
                          {savingId === r.id ? t("layout.recordingsManager.saving") : t("layout.recordingsManager.saveAudio")}
                        </span>
                      </button>
                    )}
                    <button
                      className="rec-btn danger-solid"
                      disabled={saving}
                      onClick={() => {
                        deleteRecording && deleteRecording(r.keys);
                        setConfirmId(null);
                      }}
                    >
                      <span>{t("layout.recordingsManager.confirmDelete")}</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Modal>
  );
}

export default RecordingsModal;
